import AppLink from "@/integrations/next/components/AppLink"
import { Burger, Close } from "@/ui/icons"
import { classNames, setThemeColorVar, ThemeColor } from "@punks/ui-core"
import { ReactNode, useRef } from "react"
import { LanguagePicker } from "@/ui/organisms/LanguagePicker"
import { usePageMenuPainter } from "@/ui/hooks/usePageMenuPainter"
import { LinkData } from "@/ui/types/link"
import Typography from "@/ui/atoms/Typography"

export type HeaderVariant = "default" | "transparent" | "transparentDark"

export type HeaderLink = {
  label: ReactNode
  link?: LinkData
  active?: boolean
  className?: string
}

export interface HeaderProps {
  logo: ReactNode
  variant: HeaderVariant
  position?: "fixed" | "absolute" | "default"
  onMenuClick?: () => void
  menuIcon: "burger" | "close"
  menuColor?: ThemeColor
  links: HeaderLink[]
}

const HeaderMenuPainter = ({
  menuRef,
  menuColor,
}: {
  menuRef: React.RefObject<any>
  menuColor?: ThemeColor
}) => {
  usePageMenuPainter(menuRef, menuColor)
  return <></>
}

const Header = ({
  logo,
  variant,
  position,
  onMenuClick,
  menuIcon,
  menuColor,
  links,
}: HeaderProps) => {
  const ref = useRef(null)
  return (
    <div
      ref={ref}
      style={{
        ...setThemeColorVar("--menu-color", menuColor),
      }}
      className={classNames(
        "p-6 xl:h-20 xl:py-[0.625rem] xl:px-25 flex items-center justify-between w-full",
        {
          "bg-white": variant === "default",
          "bg-transparent":
            variant === "transparent" || variant === "transparentDark",
          fixed: position === "fixed",
          absolute: position === "absolute",
        }
      )}
    >
      {position !== "fixed" && ref && (
        <HeaderMenuPainter
          menuRef={ref}
          menuColor={
            variant === "default" || variant === "transparentDark"
              ? "paperNegative"
              : "paper"
          }
        />
      )}

      <AppLink href="/" className="relative top-0 z-1">
        {logo}
      </AppLink>

      <div
        className={classNames("hidden lg:flex right-0 z-50 items-center", {
          "text-white": variant === "transparent" && !menuColor,
          "text-text":
            (variant === "default" || variant === "transparentDark") &&
            !menuColor,
          "text-[var(--menu-color)]": !!menuColor,
        })}
      >
        <div className="flex gap-12 mr-8">
          {links.map((x, i) => (
            <div key={i}>
              <AppLink href={x.link?.url}>
                <Typography
                  variant={"body2"}
                  weight={"medium"}
                  className={classNames({
                    "border-b-[2px] border-[var(--menu-color)]": x.active,
                  })}
                >
                  {x.label}
                </Typography>
              </AppLink>
            </div>
          ))}
        </div>

        <LanguagePicker
          buttonClassName={classNames("px-2 sm:px-4 py-4 xl:py-[1.5rem] flex", {
            "text-secondary": variant === "transparent",
            "text-primary": variant !== "transparent",
          })}
        />
      </div>

      <div
        className={classNames(
          "lg:hidden fixed right-0 z-50 flex items-center",
          {
            "text-white": variant === "transparent" && !menuColor,
            "text-text":
              (variant === "default" || variant === "transparentDark") &&
              !menuColor,
            "text-[var(--menu-color)]": !!menuColor,
          }
        )}
      >
        <button
          type="button"
          className="pl-2 pr-4 sm:px-6 py-6 xl:py-[1.5rem]"
          onClick={onMenuClick}
        >
          {menuIcon === "close" ? (
            <Close className="burgherMenuIcon" />
          ) : (
            <Burger className="burgherMenuIcon" width={18} height={18} />
          )}
        </button>
      </div>
    </div>
  )
}

export default Header
